import React, {useState, useEffect} from 'react';
import styled, {keyframes} from 'styled-components';
import {AstView} from 'rendition-core/types';
import {detect} from 'detect-browser';
import {AIGenLink} from 'Components/AIGenLink';

const supportedBrowsersToolTipText = `
    Supported Browsers:
    Chrome, Firefox, Edge

    Copying and Pasting not supported in Safari.
`;

interface Props {
    copyForWebFlow?: (() => void) | undefined | false;
    view: AstView | null;
    cloneDoc: () => void;
    showDownloadAssets?: boolean;
    saving?: 'unsaved' | 'saved' | 'saving';
    id?: string;
}

export const Header: React.FC<React.PropsWithChildren<Props>> = ({
    copyForWebFlow,
    view,
    saving,
    cloneDoc,
    id,
    showDownloadAssets,
}) => {
    const [showSuccessMsg, setShowSuccessMsg] = useState(false);
    const [supportedBrowser, setSupportedBrowser] = useState(true);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setShowSuccessMsg(false);
        }, 2000);
        return () => {
            clearTimeout(timeout);
        };
    }, [showSuccessMsg]);

    useEffect(() => {
        const browser = detect();

        if (browser && browser.name === 'safari') {
            setSupportedBrowser(false);
        }
    }, []);

    const ExecuteCopyForWebFlow = () => {
        if (copyForWebFlow) {
            copyForWebFlow();
        }
        setShowSuccessMsg(true);
    };

    return (
        <Nav>
            <Row>
                <Logo>
                    <Image1 src={'https://file.rendit.io/n/42lH8C3aArT5NVbYQBq6.svg'} />
                </Logo>
                <Name>Rendition</Name>
            </Row>
            <LinkGroup>
                {id && <AIGenLink id={id} />}
                {view?.meta.assetUrlZip && showDownloadAssets && (
                    <AssetButton download="zipFolder" href={view.meta.assetUrlZip}>
                        Download Assets
                    </AssetButton>
                )}
                {copyForWebFlow && !showSuccessMsg && !view?.meta.compilerOptions['buttonCompileFlag'] && (
                    <WebFlowButton
                        title={supportedBrowser ? '' : supportedBrowsersToolTipText}
                        disabled={!supportedBrowser}
                        onClick={ExecuteCopyForWebFlow}
                    >
                        Copy For Webflow
                    </WebFlowButton>
                )}
                {copyForWebFlow && showSuccessMsg && <CopiedButton>Copied!</CopiedButton>}
            </LinkGroup>
        </Nav>
    );
};

const Row = styled.div`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: 121px;
`;

const Name = styled.div`
    font-family: 'Paytone One';
    font-style: normal;
    font-weight: 400;
    font-size: 17.7643px;
    line-height: 25px;
    color: #ffffff;
`;

const LinkGroup = styled.div`
    display: flex;
    gap: 20px;
    align-items: center;
`;

const Nav = styled.div`
    background-color: #0e0c0e;
    height: 68px;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #464646;
    background-color: #1d1d20;
`;

const Logo = styled.div`
    width: 121px;
    display: flex;
    margin-top: 6px;
`;

const Image1 = styled.img`
    width: 30.2px;
    height: 30.2px;
`;

const gradientAnimation = keyframes`
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
`;

const WebFlowButton = styled.button`
    display: flex;
    font-size: 16px;
    font-family: Inter;
    font-weight: 600;
    height: 28px;
    background-color: #3b4ef7;
    color: #ffffff;
    overflow: hidden;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    padding: 5px 10px;
    border-width: 0px;
    box-sizing: content-box;
    cursor: pointer;
    text-decoration: none;
    transition: transform 1s;
    animation: ${gradientAnimation} 7s ease infinite;
    &: hover {
        box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.3);
        background-image: linear-gradient(-45deg, #1992ff, #3b4ef7, #6d35ff, #1992ff);
        transform: scale(1.03);
    }
    :disabled {
        opacity: 0.2;
        cursor: not-allowed;
    }
`;

const CopiedButton = styled.button`
    display: flex;
    font-size: 16px;
    font-family: Inter;
    font-weight: 600;
    height: 28px;
    background-color: #3b4ef7;
    color: #ffffff;
    overflow: hidden;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    padding: 5px 10px;
    border-width: 0px;
    box-sizing: content-box;
    cursor: pointer;
    text-decoration: none;
    animation: ${gradientAnimation} 1s ease infinite;
    background-image: linear-gradient(90deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
    background-size: 150% 150%;
    transition: all 400ms ease-in-out;
    box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.3);
`;

const AssetButton = styled.a`
    display: flex;
    font-size: 16px;
    font-family: Inter;
    font-weight: 600;
    height: 28px;
    background-color: grey;
    color: #ffffff;
    overflow: hidden;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    padding: 5px 10px;
    border-width: 0px;
    box-sizing: content-box;
    cursor: pointer;
    text-decoration: none;
    transition: transform 1s;
    animation: ${gradientAnimation} 7s ease infinite;
    &: hover {
        box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.3);
        background-image: linear-gradient(-45deg, white, grey, grey);
        transform: scale(1.03);
    }
`;
